import { useLayoutEffect, useRef } from 'react';

// Shrinks an element's font size until its text fits on one line. The size
// set in CSS is the maximum. Re-fits when the element's width changes (e.g.
// the header's back button appears) and once web fonts have loaded.
const useFitText = (text) => {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const fit = () => {
      el.style.fontSize = '';
      if (!el.clientWidth) return;
      const overflow = el.scrollWidth / el.clientWidth;
      if (overflow > 1) {
        const max = parseFloat(getComputedStyle(el).fontSize);
        // Text width scales linearly with font size; round down to 0.5px.
        el.style.fontSize = `${Math.floor((max / overflow) * 2) / 2}px`;
      }
    };

    fit();
    document.fonts?.ready.then(fit);

    // Only width matters (fitting changes the height), and fitting is deferred
    // to the next frame so resizing inside the observer callback doesn't
    // trigger "ResizeObserver loop" errors.
    let width = el.clientWidth;
    const observer = new ResizeObserver(() => {
      if (el.clientWidth === width) return;
      width = el.clientWidth;
      requestAnimationFrame(fit);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [text]);

  return ref;
};

export default useFitText;
